import { useLayoutEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Notification, variants } from '@eti/components';
import { Heading, Stack } from '@etg/wings';
import { TravelerForm } from '@eti/travelers';
import { css, cx, mediaQueries } from '@eti/styles';
import Media from 'react-media';
import { AgeType } from '@eti/schema-types';
import {
  BoundNew,
  BundleProduct,
  CabinBaggageTripSummary,
  CheckedBaggageSummary,
  CurrencyAmount,
  PersonalItemTripSummary,
  Price,
  SelfServiceRebookingTraveler,
  TripCharacteristics,
} from '@eti/types';
import { useProperty, useSiteContext, useSnowplowTracking, useTranslation } from '@eti/providers';
import { PersonalItem, SECOND_BAGGAGE_PER_TRAVELER } from '@eti/products';
import { getRebookingData } from '../../start/utils/rebooking';
import { getTicketingCarrierCodes } from '../utils/carriers';
import { VIRTUAL_INTERLINING } from '../../../constants/tripCharacteristics';
import { flattenProducts } from '../utils/products';
import NotificationWithOverLay from '../../../widgets/notification-with-overlay/NotificationWithOverLay';
import { hasRyanAirTicketingOrMarketingCarrier } from '../../../utils/carriersUtils';
import CFCModal from '../../../common/cfc-modal/CFCModal';
import CFCInfoCard from '../../../common/cfc-info-card/CFCInfoCard';
import useFetchCarrierSpecificText from '../utils/useFetchCarrierSpecificText';
import useTriggerInitialAsyncFormValidation from '../utils/useTriggerInitialAsyncFormValidation';
import ContactForm from './contact-form/ContactFormContainer';
import EditPassengers from './edit-passengers/EditPassengers';
import MotivationalNotification from './notifications/MotivationalNotification';
import AgentInformationFormSection from './AgentInformationFormContainer';
import ExtraProducts, { StepSection } from './TravelerDetailsExtraProducts';
import TravelerDetailsSelfTransferGuarantee from './TravelerDetailsSelfTransferGuarantee';

const wrapperStyles = (isWizardEnabled) => css`
  &:first-child {
    margin-bottom: ${isWizardEnabled ? '4px' : '20px'};
  }

  & > section:not(:last-child) {
    margin-bottom: 20px;
  }

  @media ${mediaQueries.medium.up} {
    &:first-child {
      margin-bottom: ${isWizardEnabled ? '4px' : '20px'};
    }

    & > section:not(:last-child) {
      margin-bottom: 30px;
    }
  }
`;

const fieldsetStyles = css`
  border: none;
`;

const headingClass = css`
  padding: 16px 0;
`;

const notificationStyles = css`
  margin-top: 16px;
`;

const titledCardStyles = css`
  margin-top: 8px;
`;

const TravelerDetailsForm = ({
  agentEmailAddress,
  agentPhoneNumber,
  ancillaryBundleSelectedValue,
  bounds,
  bundleData,
  cabinBaggageTripSummary,
  checkedBaggageSummary,
  countries,
  dateFormat,
  domesticCountryCode,
  emailAddress,
  emergencyPhoneNumber,
  form,
  includedCheckedBaggage,
  includedProducts = [],
  isAncillaryBundleEmptyOptionSelected,
  isAncillaryBundlingEnabled,
  isCabinBaggageAvailableOrIncluded,
  isCheckedBaggageAvailableOrIncluded,
  isMultiStop,
  isOnSectionOne,
  isSeatMapModalOpen,
  isSeatMapSelected,
  isSelfServiceRebooking,
  isTravelArrangerSelected,
  onDateOfBirthBlur = () => {},
  personalItemTripSummary,
  phoneNumber,
  products = [],
  seatMapCallback,
  selectionKey,
  selfServiceRebookingTravelers,
  setIsSeatMapModalOpen,
  shouldShowEmergencyContactInformation,
  shouldShowProductsPerTraveler,
  hasSecondaryProducts,
  setHasSecondaryProducts = () => {},
  wizardSteps,
  setWizardSteps,
  shouldShowTravelDocumentType,
  travelerFormProducts,
  travelerPrices,
  travelers,
  triggerPhoneAsyncValidation,
  triggerEmailAddressAsyncValidation,
  triggerAgentPhoneAsyncValidation,
  triggerAgentEmailAddressAsyncValidation,
  tripId,
  tripCharacteristics,
  tripOperatingCarriers,
  type,
  updateTravelerPricesOnEditPassenger,
}) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { agencyAccount } = useSiteContext();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const editPassengersEnabled = p('TravelerDetails.EditPassenger.Enabled');
  const isPrimarySecondaryEnabled = p('TravelerDetails.ProductsPrimarySecondary.Enabled');
  const isWizardEnabled =
    isPrimarySecondaryEnabled && p('TravelerDetails.ProductsPrimarySecondary.Wizard.Enabled');
  const isUrgencyBannerEnabled = p('TravelerDetails.UrgencyBanner.Enabled');
  const urgencyBannerTitle = t('TravelerDetails.UrgencyBanner.Header');
  const urgencyBannerText = t('TravelerDetails.UrgencyBanner.Text');
  const isCFCEnabled = p('IbeClient.CarrierFlightConfirmation.Enabled');
  const ticketingCarrierCodes = getTicketingCarrierCodes(bounds);
  const flattenedProducts = useMemo(() => flattenProducts(products), [products]);
  const secondBagProductDetails = flattenedProducts.filter(
    (product) => product.id === SECOND_BAGGAGE_PER_TRAVELER.id,
  );

  useTriggerInitialAsyncFormValidation(
    triggerPhoneAsyncValidation,
    triggerEmailAddressAsyncValidation,
    triggerAgentPhoneAsyncValidation,
    triggerAgentEmailAddressAsyncValidation,
    agentEmailAddress,
    agentPhoneNumber,
    emergencyPhoneNumber,
    emailAddress,
    phoneNumber,
  );

  const { carrierBaggageTexts, carrierInformationTexts } =
    useFetchCarrierSpecificText(ticketingCarrierCodes);

  const shouldSectionRender = (index) =>
    typeof wizardSteps[index] !== 'undefined' && wizardSteps[index] !== 0;

  const isSectionToggled = (index) => wizardSteps[index] === 1;

  useLayoutEffect(() => {
    if (isOnSectionOne) {
      setWizardSteps([1, 1]);
    }
  }, [isOnSectionOne, setWizardSteps]);

  const [isCFCModalOpen, setIsCFCModalOpen] = useState(false);

  const rebookingData = isSelfServiceRebooking
    ? getRebookingData(selfServiceRebookingTravelers)
    : null;
  const formConstraints = rebookingData?.formConstraints ?? null;
  const InnerWrapper = isWizardEnabled ? StepSection : Stack;
  const isSelfTransferGuaranteeEnabledInCart =
    p('SelfTransfer.InformationInCart.Enabled') &&
    tripCharacteristics.includes(VIRTUAL_INTERLINING);
  const isRyanAirTicketingOrMarketingCarrierPresent = hasRyanAirTicketingOrMarketingCarrier(
    p('IbeClient.RyanairMarketingCarriers'),
    bounds,
  );

  const handleOpenCFCModal = () => {
    setIsCFCModalOpen(true);
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: isOnSectionOne ? 'TD page' : 'Extra Services',
        action: 'Click',
        label: 'Booking process information',
        property: 'Ryanair',
      });
    }
  };

  return (
    <div className={wrapperStyles()}>
      {shouldSectionRender(0) && (
        <>
          <fieldset
            aria-labelledby="travelerDetailsForm-section"
            className={cx(fieldsetStyles, wrapperStyles(isWizardEnabled))}
            data-testid="travelerDetails-passenger-info"
            key="passenger-info"
          >
            {carrierInformationTexts?.map(
              (
                {
                  drawerContent,
                  drawerHeader,
                  notificationContent,
                  notificationLinkText,
                  notificationHeader,
                },
                index,
              ) => {
                return (
                  <NotificationWithOverLay
                    dataTestId={`carrier-notification-${index}`}
                    drawerContent={drawerContent}
                    drawerHeader={drawerHeader}
                    key={`carrierInfoNotification-${index}`}
                    notificationContent={notificationContent}
                    notificationHeader={notificationHeader}
                    notificationLinkText={notificationLinkText}
                  />
                );
              },
            )}
            {isWizardEnabled && (
              <Heading className={headingClass} id="travelerDetailsForm-section" level={1}>
                {t('TravelerDetails.PrimarySecondary.Contact.Title')}
              </Heading>
            )}
            <Stack>
              <MotivationalNotification />
              {agencyAccount && <AgentInformationFormSection countries={countries} form={form} />}
              <InnerWrapper {...(isWizardEnabled ? { expanded: isSectionToggled(0) } : {})}>
                <ContactForm
                  countries={countries}
                  form={form}
                  shouldShowEmergencyContactInformation={shouldShowEmergencyContactInformation}
                />
                {travelers?.map((traveler, index) => (
                  <div key={traveler.id}>
                    <TravelerForm
                      {...traveler}
                      bundleData={bundleData}
                      cabinBaggageTripSummary={cabinBaggageTripSummary}
                      carrierBaggageTexts={carrierBaggageTexts}
                      checkedBaggageSummary={checkedBaggageSummary}
                      dataTestId={`travelerDetails-traveler-${index}-section`}
                      dateFormat={dateFormat}
                      domesticCountryCode={domesticCountryCode}
                      form={form}
                      includedCheckedBaggage={includedCheckedBaggage}
                      isCabinBaggageAvailableOrIncluded={isCabinBaggageAvailableOrIncluded}
                      isCheckedBaggageAvailableOrIncluded={isCheckedBaggageAvailableOrIncluded}
                      isSelfServiceRebooking={isSelfServiceRebooking}
                      isTravelArranger={
                        p('TravelerDetails.TravelArranger.Enabled') &&
                        !isTravelArrangerSelected &&
                        traveler.id === '0'
                      }
                      onDateOfBirthBlur={onDateOfBirthBlur(traveler.id)}
                      secondBagProductDetails={secondBagProductDetails}
                      shouldShowProductsPerTraveler={shouldShowProductsPerTraveler}
                      shouldShowTravelDocumentType={shouldShowTravelDocumentType}
                      ticketingCarrierCodes={ticketingCarrierCodes}
                      travelerFormProducts={travelerFormProducts}
                      travelerNumber={index}
                      tripOperatingCarriers={tripOperatingCarriers}
                    >
                      <PersonalItem
                        ageType={traveler?.ageType}
                        personalItemTripSummary={personalItemTripSummary}
                        travelerId={traveler.id ?? ''}
                      />
                    </TravelerForm>
                    {isUrgencyBannerEnabled && index === 0 && (
                      <Notification
                        className={notificationStyles}
                        dataTestId="urgency-banner-price"
                        title={urgencyBannerTitle}
                        variant={variants.INFORMATION}
                      >
                        {urgencyBannerText}
                      </Notification>
                    )}
                  </div>
                ))}
                {editPassengersEnabled && (
                  <EditPassengers
                    constraints={formConstraints?.passengers}
                    selectedTripBounds={bounds}
                    selectionKey={selectionKey}
                    tripId={tripId}
                    updateTravelerPricesOnEditPassenger={updateTravelerPricesOnEditPassenger}
                  />
                )}
              </InnerWrapper>
            </Stack>
          </fieldset>
          {isSelfTransferGuaranteeEnabledInCart && <TravelerDetailsSelfTransferGuarantee />}
        </>
      )}
      <div className={wrapperStyles()} data-testid="travelerDetails-products" key="products">
        <ExtraProducts
          ancillaryBundleSelectedValue={ancillaryBundleSelectedValue}
          bundleData={bundleData}
          checkedBaggageSummary={checkedBaggageSummary}
          hasSecondaryProducts={hasSecondaryProducts}
          includedCheckedBaggage={includedCheckedBaggage}
          includedProducts={includedProducts}
          isAncillaryBundleEmptyOptionSelected={isAncillaryBundleEmptyOptionSelected}
          isAncillaryBundlingEnabled={isAncillaryBundlingEnabled}
          isCabinBaggageAvailableOrIncluded={isCabinBaggageAvailableOrIncluded}
          isCheckedBaggageAvailableOrIncluded={isCheckedBaggageAvailableOrIncluded}
          isMultiStop={isMultiStop}
          isResponsiveFlow
          isSeatMapModalOpen={isSeatMapModalOpen}
          isSeatMapSelected={isSeatMapSelected}
          isSectionToggled={isSectionToggled}
          products={products}
          seatMapCallback={seatMapCallback}
          selectionKey={selectionKey}
          setHasSecondaryProducts={setHasSecondaryProducts}
          setIsSeatMapModalOpen={setIsSeatMapModalOpen}
          shouldSectionRender={shouldSectionRender}
          travelerFormProducts={travelerFormProducts}
          travelerPrices={travelerPrices}
          travelers={travelers}
          tripCharacteristics={tripCharacteristics}
          type={type}
          wizardSteps={wizardSteps}
        />
      </div>
      {isCFCEnabled && isRyanAirTicketingOrMarketingCarrierPresent && (
        <Media query={mediaQueries.large.up}>
          {(matches) =>
            !matches && (
              <>
                <CFCInfoCard
                  className={titledCardStyles}
                  contentText={t('CarrierFlightConfirmation.InfoBox.Description')}
                  handleLinkButtonClick={handleOpenCFCModal}
                  linkButtonText={t('CarrierFlightConfirmation.InfoBox.AnchorButton.Text')}
                  title={t('CarrierFlightConfirmation.InfoBox.Title')}
                />{' '}
                <CFCModal
                  isOpen={isCFCModalOpen}
                  setIsOpen={setIsCFCModalOpen}
                  shouldShowProceedButton={false}
                />
              </>
            )
          }
        </Media>
      )}
    </div>
  );
};

TravelerDetailsForm.propTypes = {
  agentEmailAddress: PropTypes.string,
  agentPhoneNumber: PropTypes.string,
  ancillaryBundleSelectedValue: PropTypes.string,
  asyncValidate: PropTypes.func,
  bottomContent: PropTypes.shape({
    isAnyTouched: PropTypes.bool,
    isValid: PropTypes.bool,
  }),
  bottomContentRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  bounds: PropTypes.arrayOf(BoundNew),
  bundleData: BundleProduct,
  cabinBaggageTripSummary: CabinBaggageTripSummary,
  checkedBaggageSummary: CheckedBaggageSummary,
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
      phoneCountryId: PropTypes.string,
      phoneCountryPrefix: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  dateFormat: PropTypes.string,
  domesticCountryCode: PropTypes.string,
  emailAddress: PropTypes.string,
  emergencyPhoneNumber: PropTypes.string,
  form: PropTypes.string,
  hasSecondaryProducts: PropTypes.bool,
  includedCheckedBaggage: PropTypes.shape({
    pieces: PropTypes.number,
    weight: PropTypes.number,
    weightUnit: PropTypes.string,
  }),
  includedProducts: PropTypes.arrayOf(PropTypes.any),
  isAncillaryBundleEmptyOptionSelected: PropTypes.bool,
  isAncillaryBundlingEnabled: PropTypes.bool,
  isCabinBaggageAvailableOrIncluded: PropTypes.bool,
  isCheckedBaggageAvailableOrIncluded: PropTypes.bool,
  isLoading: PropTypes.bool,
  isMultiStop: PropTypes.bool,
  isOnSectionOne: PropTypes.bool,
  isSeatMapModalOpen: PropTypes.bool,
  isSeatMapSelected: PropTypes.bool,
  isSelfServiceRebooking: PropTypes.bool,
  isTravelArrangerSelected: PropTypes.bool,
  onDateOfBirthBlur: PropTypes.func,
  personalItemTripSummary: PersonalItemTripSummary,
  phoneNumber: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.any),
  seatMapCallback: PropTypes.func,
  selectionKey: PropTypes.string,
  selfServiceRebookingTravelers: PropTypes.arrayOf(SelfServiceRebookingTraveler),
  setHasSecondaryProducts: PropTypes.func,
  setIsOnSectionOne: PropTypes.func,
  setIsSeatMapModalOpen: PropTypes.func,
  setWizardSteps: PropTypes.func,
  shouldShowEmergencyContactInformation: PropTypes.bool,
  shouldShowProductsPerTraveler: PropTypes.bool,
  shouldShowSeatMapModal: PropTypes.bool,
  shouldShowTravelDocumentType: PropTypes.bool,
  showSeatMapModal: PropTypes.func,
  travelerFormProducts: PropTypes.arrayOf(PropTypes.any),
  travelerPrices: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: AgeType,
      markup: PropTypes.number,
      price: Price,
      quantity: PropTypes.number,
      taxesAndFees: PropTypes.arrayOf(
        PropTypes.shape({
          category: PropTypes.string,
          taxes: PropTypes.arrayOf(
            PropTypes.shape({
              amount: CurrencyAmount,
              code: PropTypes.string,
              title: PropTypes.string,
            }),
          ),
        }),
      ),
      totalPrice: PropTypes.number,
      totalPriceSum: PropTypes.number,
      vat: PropTypes.number,
    }),
  ),
  travelers: PropTypes.arrayOf(
    PropTypes.shape({
      age: PropTypes.number,
      ageType: PropTypes.string,
      boundIndex: PropTypes.number,
      countries: PropTypes.arrayOf(PropTypes.any),
      marketingMajorityCarrier: PropTypes.string,
      segments: PropTypes.arrayOf(PropTypes.object),
      showFrequentFlyerCard: PropTypes.bool,
      travelDocument: PropTypes.shape({
        requireTravelDocumentExpiryDate: PropTypes.oneOf(['OPTIONAL', 'REQUIRED', 'NOT_REQUIRED']),
        showTravelDocumentDateOfBirth: PropTypes.bool,
        showTravelDocumentIssueCity: PropTypes.bool,
        showTravelDocumentIssueDate: PropTypes.bool,
        showTravelDocumentNationality: PropTypes.bool,
        showTravelDocumentPassportNumber: PropTypes.bool,
      }),
      travelTime: PropTypes.string,
    }),
  ),
  triggerAgentEmailAddressAsyncValidation: PropTypes.func,
  triggerAgentPhoneAsyncValidation: PropTypes.func,
  triggerEmailAddressAsyncValidation: PropTypes.func,
  triggerPhoneAsyncValidation: PropTypes.func,
  tripCharacteristics: PropTypes.arrayOf(TripCharacteristics),
  tripId: PropTypes.string,
  tripOperatingCarriers: PropTypes.arrayOf(PropTypes.string),
  type: PropTypes.string,
  updateTravelerPricesOnEditPassenger: PropTypes.func,
  wizardSteps: PropTypes.arrayOf(PropTypes.number),
};

export default TravelerDetailsForm;
